import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SearchHeader } from "./component/search";
import config from "../../configuration/config";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import * as journifyTrackingCarEvents from "../../services/trackingCarEvents";
import { groupBy, includes, mapValues, omit, orderBy } from "lodash";
import "../../css/_home_v7.scss";

// Import existing components as requested by USER
import Car from "../../components/car";
import Offer from "../../components/offer";
import { slugCombine } from "../../utils/slugManager";
import { Price } from "../../components/__simple/_price";

export const Search = () => {
  const { t: trans, i18n } = useTranslation(["model", "info", "global", "common", "vehicales", "compare", "car"]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const q = queryParams.get("q");
  const brandID = queryParams.get("brandID");
  const groupID = queryParams.get("groupID");
  const modelCode = queryParams.get("modelCode");
  const fromHome = queryParams.get("fromHome");
  // Existing URL filters
  const typeID = queryParams.get("typeID");
  const brandText = queryParams.get("brandText");
  const groupText = queryParams.get("groupText");
  const typeText = queryParams.get("typeText");

  let lang = i18n.language || "ar";
  const languageDir = "language-" + lang;

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [supportedFilters, setSupportedFilters] = useState(false);
  const [groupsResult, setGroupResult] = useState([]);
  const [isLoding, setIsLoding] = useState(false);

  useEffect(() => {
    if (fromHome) {
      handleOnSearchClick({ sort: "desc" });
    } else if (q) {
      setSearchText(q);
      handleOnSearchClick({ sort: "desc" }, q);
    }
  }, [q, fromHome]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnSearchClick = (data, forcedSearchText = null) => {
    const effectiveSearchText = forcedSearchText !== null ? forcedSearchText : searchText;
    if (!fromHome && effectiveSearchText?.length === 0) {
      setSearchResults(0);
      setGroupResult([]);
      return;
    }
    setIsLoding(true);
    setSearchResults(0);
    setGroupResult([]);

    journifyTrackingCarEvents.trackSearch(effectiveSearchText || "Home Search " + (brandID ? "BrandID: " + brandID : "") + " " + (groupID ? "GroupID: " + groupID : "") + " " + (modelCode ? "ModelCode: " + modelCode : ""));

    if (fromHome) {
      axios
        .post(`${config.controllURL}${lang}/api/Pages/site_comprehensiveSearch`, {
          BrandID: parseInt(brandID) || null,
          GroupID: groupID || null,
          ModelCode: modelCode || null,
        })
        .then((response) => {
          let responseData = response.data ?? [];
          // Grouping logic for site_comprehensiveSearch
          responseData = responseData.map((item) => {
            let sectionType = 1;
            if (item.EntityType === "Car") {
              sectionType = 1;
            } else if (item.EntityType === "Part") {
              sectionType = 2;
            } else if (item.EntityType === "Offer") {
              if (item.offerTypeId === 2) sectionType = 106; // Vehicle Offers
              else if (item.offerTypeId === 3 || item.offerTypeId === 1) sectionType = 107; // Maintenance/Service Offers
              else sectionType = 6; // Standard Offer
            }

            return {
              ...item,
              Type: sectionType,
              SectionType: sectionType,
            };
          });
          processResults(responseData, data);
        })
        .catch((error) => {
          console.error("Error fetching home search data", error);
          setSearchResults([]);
          setIsLoding(false);
        });
    } else {
      // PRESERVE OLD GetSearchInfo LOGIC AS REQUESTED
      axios
        .post(`${config.controllURL}${lang}/api/Pages/GetSearchInfo`, {
          search: `%${effectiveSearchText}%`,
        })
        .then((response) => {
          let responseData = response.data ?? [];

          // Apply extra filters if present in URL (usually from Home Search in older versions)
          if (brandID || groupID || typeID || brandText || groupText || typeText) {
            responseData = responseData.filter((item) => {
              // Only filter vehicles (Type 1)
              if (Number(item.Type) !== 1) return true;

              let match = true;

              // 1. Brand Filter
              if (brandID && item.BrandID) {
                if (String(item.BrandID) !== String(brandID)) match = false;
              } else if (brandText) {
                const itemTitle = (item.TitleAr || item.TitleEn || "").toLowerCase();
                if (!itemTitle.includes(brandText.toLowerCase())) match = false;
              }

              // 2. Group Filter
              if (groupID) {
                const gId = item.ProductGroupID || item.CarGroupID;
                if (gId) {
                  if (String(gId) !== String(groupID)) match = false;
                } else if (groupText) {
                  const itemTitle = (item.TitleAr || item.TitleEn || "").toLowerCase();
                  const itemDesc = (item.DescriptionAr || item.DescriptionEn || item.Description || "").toLowerCase();
                  if (!itemTitle.includes(groupText.toLowerCase()) && !itemDesc.includes(groupText.toLowerCase())) match = false;
                }
              }

              // 3. Type/Model Filter
              if (typeID) {
                const tId = item.ProductTypeID || item.TypeID || item.ModelTypeID;
                if (tId) {
                  if (String(tId) !== String(typeID)) match = false;
                } else if (typeText) {
                  const itemTitle = (item.TitleAr || item.TitleEn || "").toLowerCase();
                  const itemDesc = (item.DescriptionAr || item.DescriptionEn || item.Description || "").toLowerCase();
                  if (!itemTitle.includes(typeText.toLowerCase()) && !itemDesc.includes(typeText.toLowerCase())) match = false;
                }
              }

              return match;
            });
          }
          processResults(responseData, data);
        })
        .catch((error) => {
          console.error("Error fetching data", error);
          setSearchResults([]);
          setIsLoding(false);
        });
    }
  };

  const processResults = (responseData, data) => {
    const { sort, showOptions } = { ...data };
    const tempGroupValues = mapValues(groupBy(responseData, "Type"), (fList) =>
      orderBy(
        fList.map((items) => omit(items, "Type")),
        [`Title${lang === "ar" ? "Ar" : "En"}`],
        [sort]
      )
    );
    let groupValues = {};
    Object.assign(groupValues, tempGroupValues);
    if (showOptions?.length > 0) {
      for (let key in tempGroupValues) {
        if (!includes(showOptions, parseInt(key))) {
          delete groupValues[key];
        }
      }
    }
    let finalResults = [];
    for (let item in groupValues) {
      let title = "";
      switch (parseInt(item)) {
        case 1: title = trans("global:vehicle"); break;
        case 2: title = trans("global:spare_parts"); break;
        case 3: title = trans("global:news"); break;
        case 4: title = trans("global:jobs"); break;
        case 5: title = trans("global:maintenance_services"); break;
        case 6: title = trans("global:offers"); break;
        case 106: title = lang === 'ar' ? "عروض المركبات" : "Vehicle Offers"; break;
        case 107: title = lang === 'ar' ? "عروض الصيانة" : "Maintenance Offers"; break;
        case 7: title = trans("global:campaign"); break;
        default: break;
      }
      finalResults.push({ heading: title, type: parseInt(item), items: groupValues[item] });
    }
    setSearchResults(responseData);
    setGroupResult(finalResults);
    setIsLoding(false);
  };

  return (
    <div className={"home-v7-wrapper " + languageDir}>
      <div className="container">
        <SearchHeader
          onSearch={(value) => setSearchText(value)}
          onSupportFilter={(state) => setSupportedFilters(state)}
          hasFilters={supportedFilters}
          onSearchClick={(data) => handleOnSearchClick(data)}
          resultNumber={searchResults?.length}
        />
        <div className="ruler-2 my-10"></div>
        {isLoding ? (
          <div className="ctn">
            <Skeleton count={10} borderRadius={"2rem"} width={"100%"} height={"20rem"} className="mb-10" />
          </div>
        ) : (
          <div className="search-result-container ctn">
            {groupsResult.map((group, index) => (
              <div key={index} className="sec">
                <div className="stag">{group.heading}</div>
                <div className="v-grid">
                  {group.items.map((element, idx) => {
                    const title = element[`Title${lang === "ar" ? "Ar" : "En"}`];
                    const description = element[`Description${lang === "ar" ? "Ar" : "En"}`] || element.Description;
                    const image = element?.Image;

                    if (group.type === 1) {
                      return (
                        <Car
                          key={idx}
                          id={element.GUID}
                          ProductId={element.ProductID}
                          name={title}
                          img={image}
                          year={element.Year}
                          minPrice={element.SalesPrice}
                          link={element.OtherLink}
                          group={element[`Description${lang === "ar" ? "Ar" : "En"}`] || element.Description}
                          brand={element.BrandID}
                          showPric={element.SalesPrice > 0}
                          newCar={true}
                          carData={element}
                        />
                      );
                    }

                    if (group.type === 6 || group.type === 106 || group.type === 107) {
                      return (
                        <Offer
                          key={idx}
                          name={title}
                          img={image}
                          excerpt={description}
                          slug={slugCombine(element.Slug || '', element.OtherLink || '')}
                          price={element.SalesPrice}
                          type={element.OfferTypeNameEn || element.OfferTypeNameAr || element.EntityType || element.type} // Handling both search and standard offer formats
                          endDate={element.OfferEndDate || element.endDate}
                          startDate={element.OfferStartDate || element.startDate}
                          totalDays={element.DaysRemaining || element.totalDays}
                        />
                      );
                    }

                    return (
                      <div className="vc rv on" key={idx} onClick={() => element.ExtraData && (window.location.href = element.ExtraData)}>
                        <div className="vc-img" style={{ height: "150px" }}>
                          <img src={image ? config.FilePathURL + image : "/assets/icons/search/car.svg"} alt={title} style={{ objectFit: "contain" }} />
                        </div>
                        <div className="vc-body" style={{ padding: '15px' }}>
                          <div className="vc-name" style={{ fontWeight: '800' }}>{title}</div>
                          <div className="vc-sub" style={{ fontSize: '12px', opacity: 0.7 }}>{description}</div>
                          <div className="vc-foot" style={{ marginTop: '10px' }}>
                            <Price className="vc-price" price={element.SalesPrice} />
                            <button className="vc-go">
                              <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        .v-grid {
            display: grid !important;
            grid-template-columns: repeat(4, 1fr);
            gap: 24px;
            width: 100%;
        }
        @media (max-width: 1400px) { .v-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 1100px) { .v-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px)  { .v-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};
